import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsSubmitting(true);
    
    // Mock form submission
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      setSubmitStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus(null), 5000);
    }
  };

  const contactInfo = [
    {
      icon: "Mail",
      label: "Email",
      value: "harsh.tiwari1995@gmail.com",
      link: "mailto:harsh.tiwari1995@gmail.com"
    },
    {
      icon: "Phone",
      label: "Phone",
      value: "+91 9663896862",
      link: "tel:+919663896862"
    },
    {
      icon: "MapPin",
      label: "Location",
      value: "Lucknow, India",
      link: "https://maps.google.com/?q=Lucknow,India"
    },
    {
      icon: "Calendar",
      label: "Availability",
      value: "Open to opportunities",
      link: null
    }
  ];

  const socialLinks = [
    {
      icon: "Github",
      label: "GitHub",
      url: "https://github.com/tharsh95",
      color: "hover:text-gray-900 dark:hover:text-white"
    },
    {
      icon: "Linkedin",
      label: "LinkedIn",
      url: "https://linkedin.com/in/harsh-tiwari-a5531815a",
      color: "hover:text-blue-600"
    },

  ];

  return (
    <section id="contact" className="py-20 bg-background">
      <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center space-y-4 mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground">
            Get In Touch
          </h2>
          <div className="w-20 h-1 bg-primary rounded-full mx-auto"></div>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Ready to start your next project? Let's discuss how we can work together 
            to bring your ideas to life.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-foreground mb-6">
                Let's Connect
              </h3>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                I'm always interested in hearing about new opportunities, 
                whether that's a full-time position, freelance project, or just a chat 
                about technology and development.
              </p>
            </div>

            {/* Contact Details */}
            <div className="space-y-4">
              {contactInfo?.map((info, index) => (
                <div key={index} className="flex items-center space-x-4 group">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center group-hover:bg-primary/20 transition-colors duration-200">
                    <Icon 
                      name={info?.icon} 
                      size={20} 
                      color="var(--color-primary)" 
                    />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">{info?.label}</p>
                    {info?.link ? (
                      <a
                        href={info?.link}
                        className="text-muted-foreground hover:text-primary transition-colors duration-200"
                        target={info?.link?.startsWith('http') ? '_blank' : undefined}
                        rel={info?.link?.startsWith('http') ? 'noopener noreferrer' : undefined}
                      >
                        {info?.value}
                      </a>
                    ) : (
                      <p className="text-muted-foreground">{info?.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Social Links */}
            <div className="pt-8">
              <h4 className="font-semibold text-foreground mb-4">Follow Me</h4>
              <div className="flex space-x-4">
                {socialLinks?.map((social, index) => (
                  <a
                    key={index}
                    href={social?.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-12 h-12 bg-muted rounded-lg flex items-center justify-center transition-all duration-200 hover:bg-primary hover:text-white ${social?.color}`}
                    aria-label={social?.label}
                  >
                    <Icon name={social?.icon} size={20} />
                  </a>
                ))}
              </div>
            </div>

            {/* Quick Response Promise */}
            <div className="bg-muted/50 rounded-lg p-6">
              <div className="flex items-start space-x-3">
                <Icon name="Clock" size={20} className="text-primary mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-foreground mb-1">Quick Response</h4>
                  <p className="text-sm text-muted-foreground">
                    I typically respond to messages within 24 hours. For urgent matters, 
                    feel free to call or connect on LinkedIn.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-card rounded-xl p-8 shadow-elevation-1">
            <h3 className="text-2xl font-bold text-foreground mb-6">
              Send a Message
            </h3>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  label="Full Name"
                  type="text"
                  name="name"
                  placeholder="Your name"
                  value={formData?.name}
                  onChange={handleInputChange}
                  required
                />
                <Input
                  label="Email Address"
                  type="email"
                  name="email"
                  placeholder="your.email@example.com"
                  value={formData?.email}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <Input
                label="Subject"
                type="text"
                name="subject"
                placeholder="What's this about?"
                value={formData?.subject}
                onChange={handleInputChange}
                required
              />

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Message *
                </label>
                <textarea
                  name="message"
                  rows={6}
                  placeholder="Tell me about your project or opportunity..."
                  value={formData?.message}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-background text-foreground placeholder-muted-foreground resize-none"
                />
              </div>

              {/* Submit Status */}
              {submitStatus && (
                <div className={`p-4 rounded-lg flex items-center space-x-2 ${
                  submitStatus === 'success' ?'bg-success/10 text-success' :'bg-error/10 text-error'
                }`}>
                  <Icon 
                    name={submitStatus === 'success' ? 'CheckCircle' : 'AlertCircle'} 
                    size={20} 
                  />
                  <span className="text-sm font-medium">
                    {submitStatus === 'success' ?'Message sent successfully! I\'ll get back to you soon.' :'Failed to send message. Please try again or contact me directly.'}
                  </span>
                </div>
              )}

              <Button
                type="submit"
                variant="default"
                size="lg"
                loading={isSubmitting}
                disabled={isSubmitting}
                iconName="Send"
                iconPosition="right"
                iconSize={18}
                fullWidth
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-border text-center">
              <p className="text-sm text-muted-foreground">
                Prefer email? Reach me directly at{' '}
                <a 
                  href="mailto:harsh.tiwari1995@gmail.com" 
                  className="text-primary hover:underline font-medium"
                >
                  harsh.tiwari1995@gmail.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;